/**
 * Refcounted `unsubscribe` gating, shared across every consumer of the chat
 * socket.
 *
 * A conversation's room can be wanted by more than one caller at once — the
 * open thread AND the inbox row it also appears in both want
 * `message.created` for it. `subscribe` is idempotent and free on the
 * backend when a socket is already in the room, so any number of callers can
 * emit it themselves with no coordination needed. `unsubscribe` is the one
 * that isn't safe to call independently: whoever closes last would otherwise
 * evict a room a still-open consumer needs. This module exists only to
 * answer "is anyone else still using this conversation" before that emit.
 *
 * `refCounts` deliberately survives a socket teardown: a retain held across a
 * reconnect is still a live intent to be in that room, and the gateway drops
 * room membership on disconnect anyway, so the `socket.connected` guard below
 * skipping an `unsubscribe` emit while disconnected is a no-op rather than a
 * leak.
 */
import type { Socket } from "socket.io-client";

const refCounts = new Map<string, number>();

/**
 * Call once per consumer that wants this conversation's room kept alive
 * (typically inside an effect, right after `acquireChatSocket`). The
 * returned function releases that hold — call it exactly once, e.g. from
 * the same effect's cleanup. The room's `unsubscribe` is only emitted once
 * every holder has released.
 */
export function retainConversation(
  socket: Socket,
  conversationId: string,
): () => void {
  refCounts.set(conversationId, (refCounts.get(conversationId) ?? 0) + 1);

  let released = false;
  return () => {
    if (released) return;
    released = true;

    const remaining = (refCounts.get(conversationId) ?? 1) - 1;
    if (remaining <= 0) {
      refCounts.delete(conversationId);
      if (socket.connected) {
        socket.emit("unsubscribe", { conversation_id: conversationId });
      }
    } else {
      refCounts.set(conversationId, remaining);
    }
  };
}
